import schema from "./schemas";
import migrations from "./migrations";

import Dexie from "dexie";
import * as Sentry from "@sentry/vue";

const stores = Object.fromEntries(
  Object.values(schema.models).map((model) => [
    model.tableName,
    model.formatFields(),
  ])
);

const bindModels = (db) =>
  Object.values(schema.models).forEach((model) => {
    model.prototype.db = db;
    model.prototype.relatedFields = model.relatedFields;
    model.prototype.fields = model.fields;
    model.prototype.tableName = model.tableName;
    model.prototype.dateFields = model.dateFields;
    model.prototype.dateTimeFields = model.dateTimeFields;
    db[model.tableName].mapToClass(model);
  });

// G.R 15/10/26 une migration qui lève une erreur ne bloque pas l'ouverture :
// elle remonte dans Sentry et la base monte quand même de version.
const runSafely = (version, upgrade) => (tx) =>
  new Dexie.Promise((resolve) => resolve(upgrade(tx))).catch((error) => {
    Sentry.captureException(error, { tags: { dexie_migration: version } });
  });

const makeDb = () => {
  async function clearData() {
    await this.projects.toCollection().delete();
    await this.sums.toCollection().delete();
    await this.installations.toCollection().delete();
    await this.phases.toCollection().delete();
    await this.samples.toCollection().delete();
    await this.vles.toCollection().delete();
  }
  const db = new Dexie(import.meta.env.VITE_APP_DEXIE_DB_NAME);
  Object.entries(migrations).forEach(([version, upgrade]) => {
    if (import.meta.env.DEV && Number(version) > schema.VERSION)
      throw new Error(
        `Migration ${version} is above VERSION ${schema.VERSION}`
      );
    db.version(Number(version))
      .stores(stores)
      .upgrade(runSafely(version, upgrade));
  });
  db.version(schema.VERSION).stores(stores);
  db.clearData = clearData;
  bindModels(db);
  db.models = schema.models;
  return db;
};

const db = makeDb();

export const readInstalledDatabase = async (read) => {
  const installed = new Dexie(import.meta.env.VITE_APP_DEXIE_DB_NAME);
  try {
    await installed.open();
    return await read(installed);
  } finally {
    installed.close();
  }
};

const servesDeclaredSchema = (installed) =>
  db.tables.every(({ name, schema: declared }) => {
    const found = installed.tables.find((table) => table.name === name)?.schema;
    if (found?.primKey.src !== declared.primKey.src) return false;
    const indexes = new Set(found.indexes.map(({ src }) => src));
    return declared.indexes.every(({ src }) => indexes.has(src));
  });

const ROLLBACK_REPORTED = "atmo:dexie-rollback-reported";

const isFirstRollbackReport = () => {
  try {
    if (sessionStorage.getItem(ROLLBACK_REPORTED)) return false;
    sessionStorage.setItem(ROLLBACK_REPORTED, "1");
    return true;
  } catch {
    return true;
  }
};

// G.R 15/10/26 retour arrière : une base montée par une version plus récente
// reste ouverte à sa version tant qu'elle garde toutes les tables et tous les
// index déclarés ici, pour que les modifications en attente partent.
export const openDatabase = async () => {
  try {
    await db.open();
  } catch (error) {
    if (error.name !== "VersionError") throw error;
    const installedVersion = await readInstalledDatabase((installed) =>
      servesDeclaredSchema(installed) ? installed.verno : null
    ).catch(() => null);
    if (!installedVersion) throw error;
    db.version(installedVersion).stores(stores);
    bindModels(db);
    await db.open();
    if (isFirstRollbackReport())
      Sentry.captureException(error, { level: "warning" });
  }
};

export default db;
